import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useProductOptionsQuery } from "@/modules/options/presentation/api/queries";
import type {
  ProductCreateInput,
  ProductUpdateInput,
} from "@/modules/products/domain/contracts";
import { isDevMode } from "@/shared/lib/config";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { HelpCircleIcon } from "lucide-react";
import { useCreateProduct } from "../api/queries";
import { ProductFormTour, useProductFormTour } from "../tour";
import {
  getProductCatalogConfig,
  type ProductCatalogMode,
} from "../ui/product-catalog-config";
import { ProductForm } from "../ui/ProductForm";

type ProductCreatePageProps = {
  catalogMode?: ProductCatalogMode;
};

export function ProductCreatePage({
  catalogMode = "retail",
}: ProductCreatePageProps) {
  const config = getProductCatalogConfig(catalogMode);
  const nav = useNavigate();
  const createProduct = useCreateProduct();
  const { run, handleJoyrideCallback, startTour } = useProductFormTour();

  const { data: optionsData } = useProductOptionsQuery({
    limit: 100,
    includeValues: true,
  });
  const optionOptions = optionsData?.data || [];

  const handleSubmit = async (
    values: ProductCreateInput | ProductUpdateInput,
  ) => {
    const created = await createProduct.mutateAsync(
      values as ProductCreateInput,
    );
    if (config.simplifiedForm) {
      nav({ to: config.routes.detail, params: { id: created.id } });
    } else {
      nav({ to: config.routes.list });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: config.labels.breadcrumb, to: config.routes.list },
            { label: "ສ້າງໃໝ່" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-2xl tracking-tight">
            {config.labels.createTitle}{" "}
            {catalogMode === "retail" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            )}
          </h1>
          <p className="text-muted-foreground">{config.labels.createDescription}</p>
        </div>

        <ProductForm
          catalogMode={catalogMode}
          onSubmit={handleSubmit}
          onCancel={() => nav({ to: config.routes.list })}
          onDevFill={isDevMode ? () => {} : undefined}
          submitting={createProduct.isPending}
          optionOptions={optionOptions}
        />

        {catalogMode === "retail" && (
          <ProductFormTour run={run} onCallback={handleJoyrideCallback} />
        )}
      </Main>
    </>
  );
}
